import axios from "axios";
import moment from "moment";
import i18n from "../../../i18n";
import { getBaseDateFormat } from "../../../modules/constants";
import { handleHttpErrors } from "../../../modules/http";

import { getClaims } from "../../../modules/login";

import {
  orderType,
  tablePageSizes,
} from "../../ui-library/CustomTable/constants";

// Constants
const LOADING_PARTICIPANTS = "LOADING_PARTICIPANTS";
const SET_PARTICIPANTS = "SET_PARTICIPANTS";
const RESET_STATE = "RESET_STATE";
const LOGOUT = "LOGOUT";

// Acessors
const getParticipants = (state) => state?.participants;
const getTotalItems = (state) => state?.totalItems;
const getSearchedParams = (state) => state?.searchedParams;
const getLoading = (state) => state?.loading;

const canViewParticipantPage = (state) =>
  getClaims(state, "participant.canView");
const canCreateParticipant = (state) =>
  getClaims(state, "participant.canCreate");
const canEditParticipant = (state) => getClaims(state, "participant.canEdit");
const canChangeStudyStatus = (state) =>
  getClaims(state, "study.status.canChange");
const canViewStudyStatus = (state) =>
  getClaims(state, "study.status.history.canView");
const canViewPersonalData = (state) =>
  getClaims(state, "participant.personalData.canView");
const canGenerateReport = (state) =>
  getClaims(state, "participant.report.canGenerate");
const canManageUsers = (state) =>
  getClaims(state, "participant.canManageCaregivers");
const canCreate24hRecall = (state) =>
  getClaims(state, "mealLog.entries.canCreateRecall");
const canCreatePaperRecapture = (state) =>
  getClaims(state, "mealLog.entries.canCreatePaperRecapture");

// Default state
const initialState = {
  participants: [],
  totalItems: null,
  loading: false,
  searchedParams: {
    page: 0,
    limit: tablePageSizes[0],
    search: "",
    orderBy: "date",
    order: orderType.DESC,
  },
};

export const participantsReducer = (state = initialState, action) => {
  switch (action.type) {
    case LOADING_PARTICIPANTS:
      return {
        ...state,
        loading: true,
      };
    case SET_PARTICIPANTS:
      const { data } = action;
      return {
        ...state,
        participants: data.data,
        totalItems: data.records,
        searchedParams: action.searchedParams,
        loading: false,
      };
    case RESET_STATE:
      return initialState;
    case LOGOUT:
      return initialState;
    default:
      return state;
  }
};

const resetState = () => (dispatch) => {
  dispatch({ type: RESET_STATE });
};

const fetchParticipants = (siteId, searchedParams, studyId) => (dispatch) => {
  const params = {
    ...searchedParams,
    siteId,
    studyId,
  };

  dispatch({
    type: LOADING_PARTICIPANTS,
  });

  return axios
    .get(`/api/participant/getBySite`, { params })
    .then(({ data }) => {
      dispatch({
        type: SET_PARTICIPANTS,
        data,
        searchedParams,
      });
    })
    .catch((error) => {
      // if statement to avoid to show an error when the request is canceled
      if (error?.message === "cancel") {
        return;
      }

      const defaultMessage = error.response?.data
        ? error.response.data
        : "There was an error while trying to fetch participants!";

      return handleHttpErrors(error.request?.status, {
        default: defaultMessage,
      });
    });
};

const fetchEntriesData =
  (startDate, endDate, participantId = null) =>
  (dispatch, getState) => {
    const params = {
      StartDate: startDate,
      EndDate: endDate,
      limit: 9999,
    };

    if (participantId) {
      params.ParticipantId = participantId;
    }

    return axios
      .get(`/api/diet/dailyentries/`, { params })
      .then(({ data }) => {
        const reportData = {
          entries: data.dailyEntries,
          dailyTargetId: data.dailyTargets?.key,
          generatedDate: moment().format(getBaseDateFormat()),
        };
        return Promise.resolve(reportData);
      })
      .catch((error) => {
        const defaultMessage = error.response?.data
          ? error.response.data
          : `There was an error while trying to get your meal data. ${error}`;

        return handleHttpErrors(error.request?.status, {
          default: defaultMessage,
        });
      });
  };

const fetchDailyTargetHistory = (dailyTargetId) => async (dispatch) => {
  const searchParams = {
    page: 0,
    limit: 9999,
    search: "",
    orderBy: "date",
    order: orderType.DESC,
  };

  const params = {
    ...searchParams,
    dailyTargetId: dailyTargetId,
  };

  return axios
    .get(`/api/diet/dailytarget/history`, { params })
    .then(({ data }) => {
      return Promise.resolve(data.data);
    })
    .catch((error) => {
      return handleHttpErrors(error.request?.status, {
        default: i18n.t("errorText.dietDiary.dailyTargets.get"),
      });
    });
};

export const mapStateToProps = (state) => {
  return {
    participants: getParticipants(state),
    totalItems: getTotalItems(state),
    searchedParams: getSearchedParams(state),
    loading: getLoading(state),
    canViewParticipantPage: canViewParticipantPage(state),
    canCreateParticipant: canCreateParticipant(state),
    canEditParticipant: canEditParticipant(state),
    canChangeStudyStatus: canChangeStudyStatus(state),
    canViewStudyStatus: canViewStudyStatus(state),
    canViewPersonalData: canViewPersonalData(state),
    canGenerateReport: canGenerateReport(state),
    canManageUsers: canManageUsers(state),
    canCreate24hRecall: canCreate24hRecall(state),
    canCreatePaperRecapture: canCreatePaperRecapture(state),
  };
};

export const mapDispatchToProps = {
  fetchParticipants,
  resetState,
  fetchEntriesData,
  fetchDailyTargetHistory,
};
